/**
 * 日志过滤器 - 过滤第三方库的调试日志
 *
 * 功能：
 * - 过滤 qiankun、element-plus 等第三方库的调试日志
 * - 保留应用自身的错误日志
 * - 在生产环境和开发环境都生效
 */

// 需要过滤的日志模式（正则表达式）
const FILTERED_PATTERNS = [
    /\[qiankun\]/i, // qiankun 的所有日志
    /prefetch starting/i, // qiankun prefetch 日志
    /Use cache/i, // 缓存相关日志
    /uniform-setting/i, // uniform-setting 日志
    /Backend Config/i, // 后端配置日志
    /\[MessageItem\]/i, // MessageItem 组件调试日志
    /\[createOarExtractListSource\]/i, // OAR 数据源调试日志
    /chunks 顺序/i, // chunks 调试日志
    /\[transformAttachmentToUploadedFile\]/i, // 附件转换调试日志
];

// 保存原始的 console 方法
const originalConsole = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    debug: console.debug,
};

/**
 * 安全地将对象转换为字符串（避免循环引用错误）
 */
function safeStringify(arg: any): string {
    if (typeof arg === 'string') {
        return arg;
    }

    // 基本类型直接转换
    if (arg === null || arg === undefined) {
        return String(arg);
    }

    if (typeof arg === 'number' || typeof arg === 'boolean') {
        return String(arg);
    }

    // 对于对象类型，使用 try-catch 保护
    try {
        // 检查是否是 Vue 组件实例或包含循环引用的对象
        if (arg && typeof arg === 'object') {
            // Vue 组件通常有 $el, $data 等属性
            if ('$el' in arg || '$data' in arg || 'component' in arg || 'vnode' in arg) {
                return '[Vue Component/VNode]';
            }

            // 尝试序列化，限制深度避免性能问题
            return JSON.stringify(arg, null, 0);
        }

        return String(arg);
    }
    catch (error) {
        // 如果序列化失败（循环引用或其他错误），返回类型信息
        if (error instanceof TypeError && error.message.includes('circular')) {
            return '[Circular Reference Object]';
        }
        return `[${typeof arg}]`;
    }
}

/**
 * 检查日志是否应该被过滤
 */
function shouldFilter(...args: any[]): boolean {
    const message = args.map(arg => safeStringify(arg)).join(' ');

    return FILTERED_PATTERNS.some(pattern => pattern.test(message));
}

/**
 * 创建过滤后的 console 方法
 */
function createFilteredConsole(
    originalMethod: (...args: any[]) => void,
): (...args: any[]) => void {
    return function(...args: any[]) {
        if (!shouldFilter(...args)) {
            originalMethod.apply(console, args);
        }
    };
}

/**
 * 初始化日志过滤器
 */
export function initLogFilter() {
    if(localStorage.getItem('agentic_log_filter_disabled') === 'true') {
        return false;
    }
    // 覆盖 console 方法
    console.log = createFilteredConsole(originalConsole.log);
    console.info = createFilteredConsole(originalConsole.info);
    console.debug = createFilteredConsole(originalConsole.debug);

    // console.warn 通常包含重要信息，只过滤特定模式
    console.warn = function(...args: any[]) {
        if (!shouldFilter(...args)) {
            originalConsole.warn.apply(console, args);
        }
    };

    // console.error 不过滤，保留所有错误日志
}
